import React, { useState, useMemo } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import moment from "moment";


const SIN_AMBITO = "Sin clasificar";

const MIN_NOTAS_PARA_PROMEDIO = 3;

const CompararAsesoresModal = ({
  open,
  onClose,
  asesores = [],
  periodo,
  porDia = [],
  ratingsByUser = []
}) => {
  const [a, setA] = useState("");
  const [b, setB] = useState("");

  const bloques = useMemo(() => {
    const uno = asesores.find((u) => String(u.userId) === String(a));
    const dos = asesores.find((u) => String(u.userId) === String(b));

    if (!uno || !dos) return null;

    const suyos = (u) => porDia.filter((c) => Number(c.userId) === Number(u.userId));

    const dias = [
      ...new Set([...suyos(uno), ...suyos(dos)].map((c) => c.day))
    ].sort();

    const bloqueDe = (u) => {
      const filas = suyos(u);

      const nombres = [
        ...new Set(filas.map((c) => c.name || SIN_AMBITO))
      ].sort();

      const valor = (nombre, dia) =>
        filas
          .filter((c) => (c.name || SIN_AMBITO) === nombre && c.day === dia)
          .reduce((t, c) => t + (c.total || 0), 0);

      return {
        asesor: u,
        ambitos: nombres.map((nombre) => ({
          nombre,
          porDia: dias.map((d) => valor(nombre, d)),
          total: filas
            .filter((c) => (c.name || SIN_AMBITO) === nombre)
            .reduce((t, c) => t + (c.total || 0), 0)
        })),
        totalPorDia: dias.map((d) =>
          filas.filter((c) => c.day === d).reduce((t, c) => t + (c.total || 0), 0)
        )
      };
    };

    const dosBloques = [bloqueDe(uno), bloqueDe(dos)];

    const ambitos = [
      ...new Set(dosBloques.flatMap((b) => b.ambitos.map((x) => x.nombre)))
    ].sort();

    return { dias, bloques: dosBloques, ambitos };
  }, [a, b, asesores, porDia]);

  const celda = (valor, base, key) => {
    const pct = base > 0 ? Math.round((valor / base) * 100) : null;

    return (
      <TableCell key={key} align="center">
        {valor}
        {pct !== null && (
          <span style={{ opacity: 0.7, marginLeft: 6, fontSize: 12 }}>{pct}%</span>
        )}
      </TableCell>
    );
  };

  const cerrar = () => {
    setA("");
    setB("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={cerrar} maxWidth="md" fullWidth>
      <DialogTitle>
        Comparar asesores
        {}
        {periodo && (
          <Typography
            component="span"
            style={{ fontSize: 13, opacity: 0.65, marginLeft: 8 }}
          >
            · {periodo}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Box style={{ display: "flex", gap: 12, marginBottom: 18 }}>
          {[
            ["Asesor", a, setA, b],
            ["Contra", b, setB, a]
          ].map(([etiqueta, valor, poner, otro]) => (
            <FormControl key={etiqueta} variant="outlined" size="small" fullWidth>
              <InputLabel>{etiqueta}</InputLabel>
              <Select
                value={valor}
                label={etiqueta}
                onChange={(e) => poner(e.target.value)}
              >
                {asesores
                  .filter((u) => String(u.userId) !== String(otro))
                  .map((u) => (
                    <MenuItem key={u.userId} value={u.userId}>
                      {u.userName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          ))}
        </Box>

        {!bloques ? (
          <Typography style={{ fontSize: 13.5, opacity: 0.7 }}>
            Elige dos asesores para verlos enfrentados.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  {}
                  <TableRow>
                    <TableCell />
                    {bloques.bloques.map(({ asesor }) => (
                      <TableCell
                        key={asesor.userId}
                        align="center"
                        colSpan={bloques.dias.length + 1}
                        style={{ fontWeight: 700, borderLeft: "1px solid rgba(128,128,128,0.25)" }}
                      >
                        {asesor.userName}
                        <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: 6, fontSize: 12 }}>
                          atendió {asesor.attended || 0}
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow>
                    <TableCell />
                    {bloques.bloques.map(({ asesor }) =>
                      [
                        ...bloques.dias.map((d, i) => (
                          <TableCell
                            key={asesor.userId + "-" + d}
                            align="center"
                            style={{
                              fontSize: 12,
                              opacity: 0.75,
                              borderLeft: i === 0 ? "1px solid rgba(128,128,128,0.25)" : undefined
                            }}
                          >
                            {moment(d).format("D MMM")}
                          </TableCell>
                        )),
                        <TableCell
                          key={asesor.userId + "-tot"}
                          align="center"
                          style={{ fontSize: 12, fontWeight: 700 }}
                        >
                          Total
                        </TableCell>
                      ]
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {bloques.ambitos.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={1 + bloques.bloques.length * (bloques.dias.length + 1)}
                        style={{ opacity: 0.6 }}
                      >
                        Ninguno de los dos cerró nada en este periodo.
                      </TableCell>
                    </TableRow>
                  ) : (
                    bloques.ambitos.map((nombre) => (
                      <TableRow key={nombre} hover>
                        <TableCell>{nombre}</TableCell>
                        {bloques.bloques.map((bloque) => {
                          const fila = bloque.ambitos.find((x) => x.nombre === nombre);

                          return [
                            ...bloques.dias.map((d, i) => (
                              <TableCell
                                key={bloque.asesor.userId + nombre + d}
                                align="center"
                                style={{
                                  borderLeft: i === 0 ? "1px solid rgba(128,128,128,0.25)" : undefined
                                }}
                              >
                                {fila ? fila.porDia[i] : 0}
                              </TableCell>
                            )),
                            <TableCell
                              key={bloque.asesor.userId + nombre + "-tot"}
                              align="center"
                              style={{ fontWeight: 700 }}
                            >
                              {fila ? fila.total : 0}
                            </TableCell>
                          ];
                        })}
                      </TableRow>
                    ))
                  )}

                  {}
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>Cerrados</TableCell>
                    {bloques.bloques.map((bloque) => [
                      ...bloque.totalPorDia.map((v, i) => (
                        <TableCell
                          key={bloque.asesor.userId + "-c" + i}
                          align="center"
                          style={{
                            fontWeight: 700,
                            borderLeft: i === 0 ? "1px solid rgba(128,128,128,0.25)" : undefined
                          }}
                        >
                          {v}
                        </TableCell>
                      )),
                      celda(
                        bloque.totalPorDia.reduce((t, v) => t + v, 0),
                        bloque.asesor.attended || 0,
                        bloque.asesor.userId + "-ctot"
                      )
                    ])}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography style={{ fontSize: 12, opacity: 0.65, marginTop: 10, lineHeight: 1.4 }}>
              El porcentaje es sobre lo que atendió cada uno, no sobre el total del
              equipo: así se pueden comparar aunque hayan llevado volúmenes muy
              distintos.
            </Typography>

            {}
            <Box style={{ display: "flex", gap: 12, marginTop: 16 }}>
              {bloques.bloques.map(({ asesor }) => {
                const nota = ratingsByUser.find(
                  (r) => String(r.userId) === String(asesor.userId)
                );

                const notaFiable =
                  nota && (nota.total || 0) >= MIN_NOTAS_PARA_PROMEDIO;

                return (
                  <Box
                    key={asesor.userId}
                    style={{
                      flex: 1,
                      border: "1px solid rgba(128,128,128,0.25)",
                      borderRadius: 6,
                      padding: "10px 14px"
                    }}
                  >
                    <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                      {asesor.userName}
                    </Typography>

                    <Box style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <Typography style={{ fontSize: 12.5, opacity: 0.75 }}>
                        Tiempo de atención
                      </Typography>
                      <Typography style={{ fontSize: 12.5, fontWeight: 600 }}>
                        {asesor.timeP50 != null ? (
                          <>
                            {asesor.timeP50} min
                            {asesor.timeP90 != null && (
                              <span style={{ opacity: 0.6, fontWeight: 400 }}>
                                {" "}(P90: {asesor.timeP90} min)
                              </span>
                            )}
                          </>
                        ) : (
                          <span style={{ opacity: 0.5, fontWeight: 400 }}>Sin datos</span>
                        )}
                      </Typography>
                    </Box>

                    <Box style={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography style={{ fontSize: 12.5, opacity: 0.75 }}>
                        Calificación del cliente
                      </Typography>
                      <Typography style={{ fontSize: 12.5, fontWeight: 600 }}>
                        {notaFiable ? (
                          <>
                            {nota.avg} / 3
                            <span style={{ opacity: 0.6, fontWeight: 400 }}>
                              {" "}({nota.total} {nota.total === 1 ? "nota" : "notas"})
                            </span>
                          </>
                        ) : nota && nota.total > 0 ? (
                          <span style={{ opacity: 0.5, fontWeight: 400 }}>
                            Pocas respuestas ({nota.total})
                          </span>
                        ) : (
                          <span style={{ opacity: 0.5, fontWeight: 400 }}>Sin notas</span>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={cerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompararAsesoresModal;
